/**
 * hvsin — Mod 容器实现
 *
 * 从 SSRApplication 提取的核心容器逻辑，剥离了 HTTP/SSR/Cron 等业务功能。
 * 仅负责：Mod 生命周期管理、服务依赖解析、接口注册表、事件系统、注册式分发。
 */
import { ModState as ModStateEnum, EVENT_BUS_INTERFACE_TAG } from './types.js';
import { instantiateService, parseDependencyRef } from './resolver.js';
import { EventBusService } from './event-bus.js';
/**
 * Mod 容器
 *
 * 管理 Mod 的生命周期（加载/卸载/重新加载），
 * 处理 Mod 间的依赖关系，
 * 内部实现服务依赖解析，
 * 通过注册式分发机制将服务接口通知给各功能域插件。
 */
export class ModContainer {
    /**
     * 构造函数：初始化内置 EventBusService 并注册到接口注册表
     *
     * 容器在创建时自动将 EventBusService 实例注册为 IEventBusService，
     * 业务 Mod 可通过 dependencies: ['IEventBusService'] 获取实例。
     */
    constructor() {
        /** 已加载 Mod：modName → ILoadedMod */
        this.loadedMods = new Map();
        /** Mod 状态：modName → ModState */
        this.modStates = new Map();
        /** 接口注册表：serviceInterfaceTag → { serviceTag, instance } */
        this.interfaceRegistry = new Map();
        /** 扁平服务池（活视图）：`${modName}.${serviceTag}` → 服务实例 */
        this.flatServices = new Map();
        /** 内置事件总线服务实例 */
        this.eventBus = new EventBusService();
        this.interfaceRegistry.set(EVENT_BUS_INTERFACE_TAG, {
            serviceTag: 'eventBusService',
            instance: this.eventBus,
        });
    }
    // ==================== Mod 加载 ====================
    /**
     * 加载 Mod
     *
     * 按 Mod 声明顺序加载，每个 Mod 加载前会检查：
     * 1. modDependencies 中声明的 Mod 是否已加载
     * 2. serviceDefs.dependencies 中的跨 Mod 引用目标 Mod 是否已加载
     */
    async loadMods(mods) {
        for (const mod of mods) {
            // 跳过已加载的 Mod
            if (this.loadedMods.has(mod.name)) {
                continue;
            }
            await this.loadModInternal(mod);
        }
    }
    /**
     * 内部加载方法（核心逻辑）
     */
    async loadModInternal(mod) {
        const modName = mod.name;
        // 1. 检查 Mod 依赖是否已加载
        for (const dep of mod.modDependencies || []) {
            if (!this.loadedMods.has(dep)) {
                throw new Error(`Mod "${modName}" depends on "${dep}" which is not loaded`);
            }
        }
        // 2. 自动校验跨 Mod 依赖引用（从 serviceDefs.dependencies 中提取）
        const referencedMods = new Set();
        for (const def of mod.serviceDefs) {
            for (const depRef of def.dependencies ?? []) {
                const { modName: targetMod } = parseDependencyRef(depRef);
                if (targetMod) {
                    referencedMods.add(targetMod);
                }
            }
        }
        for (const targetMod of referencedMods) {
            if (!this.loadedMods.has(targetMod)) {
                throw new Error(`Mod "${modName}" references service from "${targetMod}" ` +
                    `but it is not loaded. Add "${targetMod}" to modDependencies ` +
                    `or ensure it is loaded first.`);
            }
        }
        // 3. 设置状态为 Loading
        this.setModState(modName, ModStateEnum.Loading);
        try {
            // 4. 创建服务实例映射
            const services = new Map();
            // 5. 实例化所有服务
            for (const def of mod.serviceDefs) {
                const instance = instantiateService(def, services, this.loadedMods, this.interfaceRegistry);
                services.set(def.serviceTag, instance);
                // 6. 注册到接口注册表（供接口式依赖注入使用）
                if (def.serviceInterfaceTag) {
                    this.interfaceRegistry.set(def.serviceInterfaceTag, {
                        serviceTag: def.serviceTag,
                        instance,
                    });
                }
            }
            // 7. 创建已加载 Mod 对象
            const loadedMod = {
                name: modName,
                state: ModStateEnum.Loaded,
                services,
                sourceMod: mod,
                loadedAt: Date.now(),
            };
            this.loadedMods.set(modName, loadedMod);
            // 维护扁平服务池（getAllServices 的数据源，随加载/卸载实时更新）
            for (const [serviceTag, instance] of services.entries()) {
                this.flatServices.set(`${modName}.${serviceTag}`, instance);
            }
            // 8. 事件通知：emit service:loaded
            await this.dispatchServiceHandlers(mod, loadedMod);
            // 9. 更新状态并发出事件
            this.setModState(modName, ModStateEnum.Loaded);
            await this.eventBus.emit('mod:loaded', {
                modName,
                mod: loadedMod,
                timestamp: Date.now(),
            });
        }
        catch (error) {
            // 错误处理
            this.setModState(modName, ModStateEnum.Error);
            await this.eventBus.emit('mod:error', {
                modName,
                error: error,
                timestamp: Date.now(),
            });
            throw error;
        }
    }
    /**
     * 事件通知：遍历 Mod 中的所有服务，emit 'service:loaded' 事件
     *
     * 对每个已实例化的服务发出 service:loaded 事件：
     * — 载荷: { modName, serviceTag, serviceInterfaceTag?, instance, loadedMod }
     * — 即使没有 serviceInterfaceTag 的服务也会发出事件（便于业务 Mod 监听所有服务加载）
     *
     * 所有分发逻辑由 EventBusService 订阅者自行处理，容器核心不再关心具体接口名。
     */
    async dispatchServiceHandlers(mod, loadedMod) {
        for (const def of mod.serviceDefs) {
            const instance = loadedMod.services.get(def.serviceTag);
            if (!instance)
                continue;
            await this.eventBus.emit('service:loaded', {
                modName: mod.name,
                serviceTag: def.serviceTag,
                serviceInterfaceTag: def.serviceInterfaceTag,
                instance,
                loadedMod,
            });
        }
    }
    // ==================== Mod 卸载 ====================
    /**
     * 卸载 Mod
     */
    async unloadMod(modName) {
        const loadedMod = this.loadedMods.get(modName);
        if (!loadedMod)
            return;
        this.setModState(modName, ModStateEnum.Unloading);
        try {
            // 1. 对每个服务 emit service:unloaded 事件（在清理前发出，订阅者仍可访问实例）
            if (loadedMod.sourceMod) {
                for (const def of loadedMod.sourceMod.serviceDefs) {
                    const instance = loadedMod.services.get(def.serviceTag);
                    if (instance) {
                        await this.eventBus.emit('service:unloaded', {
                            modName,
                            serviceTag: def.serviceTag,
                            serviceInterfaceTag: def.serviceInterfaceTag,
                            instance,
                            loadedMod,
                        });
                    }
                }
            }
            // 2. 清理服务实例
            loadedMod.services.clear();
            this.loadedMods.delete(modName);
            // 3. 清理扁平服务池中属于该 Mod 的条目
            const flatPrefix = `${modName}.`;
            for (const key of [...this.flatServices.keys()]) {
                if (key.startsWith(flatPrefix)) {
                    this.flatServices.delete(key);
                }
            }
            // 4. 清理接口注册表中属于该 Mod 的条目
            for (const [tag, entry] of this.interfaceRegistry.entries()) {
                if (entry.serviceTag === loadedMod.sourceMod?.serviceDefs.find(d => d.serviceTag === entry.serviceTag)?.serviceTag) {
                    // 简化判断：如果该 serviceTag 属于被卸载 Mod 则移除
                    const belongsToMod = loadedMod.sourceMod?.serviceDefs.some(d => d.serviceTag === entry.serviceTag);
                    if (belongsToMod) {
                        this.interfaceRegistry.delete(tag);
                    }
                }
            }
            // 5. 更新状态并发出事件
            this.setModState(modName, ModStateEnum.Unloaded);
            await this.eventBus.emit('mod:unloaded', {
                modName,
                timestamp: Date.now(),
            });
        }
        catch (error) {
            this.setModState(modName, ModStateEnum.Error);
            throw error;
        }
    }
    /**
     * 重新加载 Mod
     */
    async reloadMod(modName, newMod) {
        const existing = this.loadedMods.get(modName);
        const modDef = newMod ?? existing?.sourceMod;
        if (!modDef) {
            throw new Error(`Mod "${modName}" not found and no new mod provided`);
        }
        await this.unloadMod(modName);
        await this.loadModInternal(modDef);
    }
    // ==================== 查询方法 ====================
    /** 检查 Mod 是否已加载 */
    isModLoaded(modName) {
        return this.loadedMods.has(modName);
    }
    /** 获取已加载 Mod */
    getLoadedMod(modName) {
        return this.loadedMods.get(modName);
    }
    /** 获取 Mod 状态 */
    getModState(modName) {
        return this.modStates.get(modName) ?? ModStateEnum.Unloaded;
    }
    /** 获取服务实例 */
    getService(modName, serviceTag) {
        const loadedMod = this.loadedMods.get(modName);
        if (!loadedMod) {
            throw new Error(`Mod not found: ${modName}`);
        }
        const service = loadedMod.services.get(serviceTag);
        if (!service) {
            throw new Error(`Service not found: ${modName}.${serviceTag}`);
        }
        return service;
    }
    /**
     * 获取扁平服务池（活视图）
     *
     * 键为 `${modName}.${serviceTag}`，值为服务实例。
     * 返回内部 Map 的活引用：后续 Mod 加载/卸载会实时反映到该视图，
     * 适合作为请求上下文等服务池来源（无需重建快照）。
     *
     * 包含所有已加载 Mod 的全部服务（含框架服务）。
     */
    getAllServices() {
        return this.flatServices;
    }
    // ==================== 私有方法 ====================
    /** 设置 Mod 状态 */
    setModState(modName, state) {
        const oldState = this.modStates.get(modName);
        this.modStates.set(modName, state);
        // 发出状态变化事件
        if (oldState !== state) {
            this.eventBus.emit('mod:state_changed', {
                modName,
                oldState,
                newState: state,
                timestamp: Date.now(),
            });
        }
    }
}
//# sourceMappingURL=container.js.map